import React, { useState, useEffect } from 'react';
import { poiLayerManager, POILayerGroup, POILayerState } from '../services/POILayerManager';
// Category definitions and helpers now live in src/constants/Categories.ts (DUP-12)
import { getCategoryInfo as getIFCCategoryInfo } from '../constants/Categories';

interface FlattenedPOIType extends POILayerState {
  groupName: string;
  groupId: string;
  groupIcon: string;
  groupColor: string;
}

interface CategoryHeader {
  id: string;
  name: string;
  icon: string;
  color: string;
  totalCount: number;
  visible: boolean;
}

interface POILayerControlsProps {
  onLayerChange?: (visibleLayers: string[]) => void;
  compact?: boolean; // For minimap or embedded views
  variant?: 'default' | 'settings'; // For settings panel styling
}

const POILayerControls: React.FC<POILayerControlsProps> = ({ onLayerChange, compact = false, variant = 'default' }) => {
  const [flattenedTypes, setFlattenedTypes] = useState<FlattenedPOIType[]>([]);
  const [categoryHeaders, setCategoryHeaders] = useState<CategoryHeader[]>([]);
  const [expandedPOITypes, setExpandedPOITypes] = useState<Set<string>>(new Set()); // For individual POI expansion
  const [allLayersVisible, setAllLayersVisible] = useState(() => {
    // Initialize from POILayerManager's current state, not hardcoded true
    return poiLayerManager.getAllLayersVisible();
  });
  const [poisExpanded, setPoisExpanded] = useState(true); // For compact view POI section collapsibility

  useEffect(() => {
    // Initialize flattened data
    setFlattenedTypes(poiLayerManager.getFlattenedPOITypes());
    setCategoryHeaders(poiLayerManager.getCategoryHeaders());

    // Listen for layer state updates
    const handleLayerStateUpdate = ({ layers }: { layers: POILayerGroup[] }) => {
      setFlattenedTypes(poiLayerManager.getFlattenedPOITypes());
      setCategoryHeaders(poiLayerManager.getCategoryHeaders());
      if (onLayerChange) {
        const visibleLayers = poiLayerManager.getFlattenedPOITypes()
          .filter(type => type.visible)
          .map(type => type.id);
        onLayerChange(visibleLayers);
      }
    };

    const handleAllLayersVisibilityChange = ({ visible }: { visible: boolean }) => {
      setAllLayersVisible(visible);
    };

    poiLayerManager.on('layer-state-updated', handleLayerStateUpdate);
    poiLayerManager.on('all-layers-visibility-changed', handleAllLayersVisibilityChange);

    return () => {
      poiLayerManager.off('layer-state-updated', handleLayerStateUpdate);
      poiLayerManager.off('all-layers-visibility-changed', handleAllLayersVisibilityChange);
    };
  }, [onLayerChange]);

  const togglePOITypeExpansion = (poiTypeId: string) => {
    const newExpanded = new Set(expandedPOITypes);
    if (newExpanded.has(poiTypeId)) {
      newExpanded.delete(poiTypeId);
    } else {
      newExpanded.add(poiTypeId);
    }
    setExpandedPOITypes(newExpanded);
  };

  const handleGroupToggle = (groupId: string) => {
    poiLayerManager.toggleGroupVisibility(groupId);
  };

  const handlePOITypeToggle = (layerId: string, event?: React.ChangeEvent<HTMLInputElement>) => {
    if (event) {
      event.stopPropagation();
    }
    poiLayerManager.toggleLayerVisibility(layerId);
  };

  const handleAllLayersToggle = () => {
    poiLayerManager.toggleAllLayersVisibility();
  };

  const togglePoisExpansion = () => {
    setPoisExpanded(!poisExpanded);
  };

  const totalPOICount = categoryHeaders.reduce((sum, header) => sum + header.totalCount, 0);

  // Settings view for settings panel - matches other settings items exactly
  if (variant === 'settings') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        borderRadius: '8px',
        background: allLayersVisible ? 'var(--dt-accent-tint)' : 'var(--dt-surface-2)',
        cursor: 'pointer'
      }}
      onClick={handleAllLayersToggle}
      >
        <input
          type="checkbox"
          checked={allLayersVisible}
          onChange={handleAllLayersToggle}
          style={{ margin: 0, accentColor: 'var(--dt-accent)' }}
        />
        <span style={{
          color: 'var(--dt-text)',
          fontSize: '.86rem',
          fontWeight: 600,
        }}>
          POI Layers ({totalPOICount})
        </span>
      </div>
    );
  }

  // Compact view for minimap or embedded use
  if (compact) {
    return (
      <div>
        {/* POI Header - collapsible like other layer items */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 10px',
          borderRadius: '8px',
          background: allLayersVisible ? 'var(--dt-accent-tint)' : 'var(--dt-surface-2)',
          marginBottom: '6px'
        }}>
          <button
            onClick={togglePoisExpansion}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--dt-text-3)',
              cursor: 'pointer',
              fontSize: '10px',
              padding: '2px',
              transform: poisExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          >
            ▶
          </button>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '.86rem',
            fontWeight: 600,
            color: 'var(--dt-text)',
            cursor: 'pointer',
            flex: 1,
          }}>
            <input
              type="checkbox"
              checked={allLayersVisible}
              onChange={handleAllLayersToggle}
              style={{ margin: 0, accentColor: 'var(--dt-accent)' }}
            />
            POI layers
            <span style={{ marginInlineStart: 'auto', fontSize: '.72rem', color: 'var(--dt-text-3)', fontWeight: 600 }}>
              {totalPOICount}
            </span>
          </label>
        </div>

        {/* POI Categories - grouped by IFC category to match Systems page exactly */}
        {poisExpanded && (
          <div style={{ marginInlineStart: '4px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {flattenedTypes.map(poiType => {
              // Use the IFC category name directly (stored in name field after POIService fix)
              const categoryName = poiType.name || 'Other';
              const categoryInfo = getIFCCategoryInfo(categoryName);

              return (
                <label key={poiType.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '.82rem',
                  cursor: 'pointer',
                  opacity: allLayersVisible ? 1 : 0.5,
                  padding: '6px 9px',
                  borderRadius: '0 6px 6px 0',
                  borderInlineStart: `3px solid ${categoryInfo.color}`,
                  background: poiType.visible ? `${categoryInfo.color}1f` : 'transparent'
                }}>
                  <input
                    type="checkbox"
                    checked={poiType.visible && allLayersVisible}
                    onChange={() => poiLayerManager.toggleLayerVisibility(poiType.id)}
                    disabled={!allLayersVisible}
                    style={{ margin: 0, accentColor: categoryInfo.color }}
                  />
                  <span style={{
                    color: poiType.visible ? 'var(--dt-text)' : 'var(--dt-text-3)',
                    flex: 1,
                  }}>
                    {categoryName}
                  </span>
                  <span style={{
                    fontSize: '.7rem',
                    fontWeight: 700,
                    padding: '1px 7px',
                    borderRadius: '6px',
                    background: 'var(--dt-surface-3)',
                    color: 'var(--dt-text-2)',
                  }}>
                    {poiType.count}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Full view for settings panel
  return (
    <div style={{
      background: 'var(--dt-surface)',
      borderRadius: 'var(--dt-r)',
      border: '1px solid var(--dt-border)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        borderBottom: '1px solid var(--dt-border)'
      }}>
        <div>
          <h3 style={{
            margin: 0,
            fontSize: '1rem',
            fontWeight: 700,
            color: 'var(--dt-text)'
          }}>
            POI Layers
          </h3>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '.78rem',
            color: 'var(--dt-text-3)'
          }}>
            {totalPOICount} points of interest
          </p>
        </div>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: 'var(--dt-surface-2)',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '.78rem',
          fontWeight: 600,
          color: 'var(--dt-text-2)',
        }}>
          <input
            type="checkbox"
            checked={allLayersVisible}
            onChange={handleAllLayersToggle}
            style={{
              margin: 0,
              accentColor: 'var(--dt-accent)'
            }}
          />
          {allLayersVisible ? 'Hide All' : 'Show All'}
        </label>
      </div>

      {/* Flattened POI Types with Category Headers */}
      <div style={{ padding: '8px' }}>
        {(() => {
          let currentGroupId = '';
          return flattenedTypes.map(poiType => {
            const showGroupHeader = poiType.groupId !== currentGroupId;
            currentGroupId = poiType.groupId;

            const groupHeader = categoryHeaders.find(h => h.id === poiType.groupId);

            return (
              <React.Fragment key={poiType.id}>
                {showGroupHeader && groupHeader && (() => {
                  const groupInfo = getIFCCategoryInfo(groupHeader.name);
                  return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 8px 6px 8px',
                    marginTop: currentGroupId === flattenedTypes[0].groupId ? '0' : '16px',
                    borderBottom: `1px solid ${groupInfo.color}30`,
                    marginBottom: '8px'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontSize: '.9rem',
                      fontWeight: 700,
                      color: 'var(--dt-text)'
                    }}>
                      <input
                        type="checkbox"
                        checked={groupHeader.visible && allLayersVisible}
                        onChange={() => handleGroupToggle(groupHeader.id)}
                        disabled={!allLayersVisible}
                        style={{
                          margin: 0,
                          accentColor: groupInfo.color
                        }}
                      />
                      {groupHeader.name}
                      <span style={{
                        background: groupInfo.color,
                        color: '#fff',
                        fontSize: '.62rem',
                        fontWeight: 700,
                        padding: '2px 7px',
                        borderRadius: '999px'
                      }}>
                        {groupHeader.totalCount}
                      </span>
                    </label>
                  </div>
                  );
                })()}

                {/* Get category info for this POI type */}
                {(() => {
                  const typeInfo = getIFCCategoryInfo(poiType.subcategory || poiType.name);
                  return (
                <div style={{
                  marginBottom: '6px',
                  marginInlineStart: '16px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: poiType.visible ? `${typeInfo.color}14` : 'var(--dt-surface-2)',
                    borderRadius: '8px',
                    border: `1px solid ${poiType.visible ? `${typeInfo.color}40` : 'var(--dt-border)'}`,
                    borderInlineStart: `3px solid ${typeInfo.color}`,
                    cursor: 'pointer',
                    opacity: allLayersVisible ? 1 : 0.5
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <input
                        type="checkbox"
                        checked={poiType.visible && allLayersVisible}
                        onChange={() => handlePOITypeToggle(poiType.id)}
                        disabled={!allLayersVisible}
                        style={{
                          margin: 0,
                          accentColor: typeInfo.color
                        }}
                      />
                      <span style={{
                        fontSize: '.85rem',
                        color: poiType.visible ? 'var(--dt-text)' : 'var(--dt-text-3)',
                        fontWeight: 500
                      }}>
                        {poiType.name}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        background: `${typeInfo.color}20`,
                        color: typeInfo.color,
                        fontSize: '.68rem',
                        padding: '2px 7px',
                        borderRadius: '6px',
                        fontWeight: 700
                      }}>
                        {poiType.count}
                      </span>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          togglePOITypeExpansion(poiType.id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--dt-text-3)',
                          cursor: 'pointer',
                          fontSize: '10px',
                          padding: '2px',
                          transform: expandedPOITypes.has(poiType.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}
                        title="Expand to see individual assets"
                      >
                        ▶
                      </button>
                    </div>
                  </label>

                  {/* Individual POIs (expandable) */}
                  {expandedPOITypes.has(poiType.id) && (
                    <div style={{
                      marginTop: '4px',
                      marginInlineStart: '20px',
                      padding: '8px 10px',
                      background: 'var(--dt-surface-2)',
                      borderRadius: '6px',
                      border: '1px solid var(--dt-border)'
                    }}>
                      <div style={{
                        fontSize: '.68rem',
                        color: 'var(--dt-text-3)',
                        marginBottom: '4px'
                      }}>
                        Individual assets ({poiLayerManager.getPOIsForSubcategory(poiType.id).length}):
                      </div>
                      {poiLayerManager.getPOIsForSubcategory(poiType.id).slice(0, 5).map((poiId, index) => (
                        <div key={poiId} style={{
                          fontSize: '.64rem',
                          color: 'var(--dt-text-3)',
                          padding: '2px 0',
                          fontFamily: 'ui-monospace, monospace'
                        }}>
                          {'•'} Asset #{index + 1} (ID: {poiId.substring(0, 8)}...)
                        </div>
                      ))}
                      {poiLayerManager.getPOIsForSubcategory(poiType.id).length > 5 && (
                        <div style={{
                          fontSize: '.64rem',
                          color: 'var(--dt-text-3)',
                          fontStyle: 'italic',
                          marginTop: '2px'
                        }}>
                          ... and {poiLayerManager.getPOIsForSubcategory(poiType.id).length - 5} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
                  );
                })()}
              </React.Fragment>
            );
          });
        })()}
      </div>

      {/* Reset Button */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid var(--dt-border)',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <button
          onClick={() => poiLayerManager.resetToDefaults()}
          style={{
            background: 'none',
            border: '1px solid var(--dt-border)',
            borderRadius: '8px',
            color: 'var(--dt-text-2)',
            cursor: 'pointer',
            fontSize: '.78rem',
            fontWeight: 600,
            padding: '6px 12px',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'var(--dt-surface-2)';
            e.currentTarget.style.color = 'var(--dt-text)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.color = 'var(--dt-text-2)';
          }}
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
};

export default POILayerControls;
